import { Formik } from "formik";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import Select from "../../components/Select";
import { getServiceEntryDropdown } from "../../store/Slices/IssuesSlice";
import { fetchVehicleDetail, fetchVehicleDropdown } from "../../store/Slices/ServiceSlice";
import { getUserDropdown } from "../../store/Slices/UserSlice";
import { financialApprovalStatus, getServiceEntryByVehicleDropdown, getVendorsDropdown, getWorkOrderById, getWorkOrderTagsDropdown, operationalApprovalStatus, updateWorkOrder } from "../../store/Slices/WorkOrderSlice";
import { workOrderStatusOptions } from "../../utils/Dropdowns";
import { formatDate, hasPermission } from "../../utils/helper";
import { WorkOrderValidationSchema } from "../../utils/Schema";
import { closedIcon, inProgressIcon, notificationIcon, openIcon, pendingIcon, resolvedIcon } from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import WorkHistory from "./WorkHistory";

const WorkOrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  const { loading, vehicleData } = useSelector((state) => state.services);
  const { userDropdownLoading, userDropdownData } = useSelector((state) => state.users);
  const { getVendorsDropdownData, getVendorsDropdownLoading, getServiceEntryByVehicleDropdownData, getServiceEntryByVehicleDropdownLoading } = useSelector((state) => state.workOrder);

  const { workOrderByIdData, workOrderByIdLoading, updateWorkOrderLoading, getWorkOrderTagsDropdownData,
    getWorkOrderTagsDropdownLoading } = useSelector((state) => state.workOrder);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [discountPercent, setDiscountPercent] = useState(parseFloat(workOrderByIdData?.discount_amount) || 0);
  const [taxPercent, setTaxPercent] = useState(parseFloat(workOrderByIdData?.tax_amount) || 0);
  const [linkIssues, setLinkIssues] = useState([]);
  const [summaryCost, setSummaryCost] = useState([]);

  const canUpdate = hasPermission("work-orders", "update");
  const canFinanceApproval = hasPermission("work-orders", "finance-approval");
  const canOperationalApproval = hasPermission("work-orders", "operations-approval");

  useEffect(() => {
    dispatch(getUserDropdown());
    dispatch(fetchVehicleDropdown());
    dispatch(getServiceEntryDropdown());
    dispatch(getVendorsDropdown());
    dispatch(getWorkOrderTagsDropdown())
  }, []);

  useEffect(() => {
    dispatch(getWorkOrderById(id));
  }, [id])

  useEffect(() => {
    if (workOrderByIdData?.asset_id) {
      dispatch(getServiceEntryByVehicleDropdown(workOrderByIdData.asset_id));
    }
  }, [workOrderByIdData?.asset_id])

  useEffect(() => {
    if (workOrderByIdData) {

      const allIssues = workOrderByIdData.services.flatMap(service => service.service_issues || []);
      setLinkIssues(allIssues);

      setSummaryCost(workOrderByIdData.services)

      setUploadedFiles(
        workOrderByIdData.images.map((img) => ({
          fileName: img.file_path,
          originalName: img.file_path,
          previewUrl: null,
          backend: true,
        }))
      );
    }
  }, [workOrderByIdData]);

  const userOptions = userDropdownData && userDropdownData.map((user) => ({
    value: user.value,
    label: user.label,
    image: user.image,
    showIcon: true,
  }));

  const vendorOptions = getVendorsDropdownData && getVendorsDropdownData.map(vendor => ({
    value: vendor.value,
    label: vendor.title,
  }));

  const serviceEntryOptions = getServiceEntryByVehicleDropdownData && getServiceEntryByVehicleDropdownData.map(service => ({
    value: service.id,
    label: service.service_id,
    service_issues: service.service_issues,
    services: service
  }));

  const tagsOptions = getWorkOrderTagsDropdownData && getWorkOrderTagsDropdownData.map(tag => ({
    value: tag?.tag_name,
    label: tag?.tag_name,
  }));

  const repairClassOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'unscheduled', label: 'Unscheduled' }
  ]

  const statusHandler = async (status) => {
    try {

      if (canFinanceApproval && workOrderByIdData?.finance_status === "pending") {
        await dispatch(
          financialApprovalStatus({
            work_order_id: parseInt(id),
            approval_status: status,
          })
        ).unwrap();
      }
      else if (canOperationalApproval && workOrderByIdData?.operation_status === "pending") {
        await dispatch(
          operationalApprovalStatus({
            work_order_id: parseInt(id),
            approval_status: status,
          })
        ).unwrap();
      }

      successNotify("Status updated successfully");
      navigate("/work-orders");
      // dispatch(getWorkOrderById(id));
    } catch (error) {
      errorNotify(error);
    }
  };

  const handleVehicleChange = async (selectedOption, setFieldValue) => {
    // setSelectedVehicle(selectedOption);
    setFieldValue("vehicle_id", selectedOption?.value || "");

    if (selectedOption) {
      const response = await dispatch(
        fetchVehicleDetail({
          vehicleId: selectedOption.value,
          vehicleBrandId: selectedOption.brand_id,
          ref_id: selectedOption.ref_id,
        })
      ).unwrap();

      setFieldValue("odo_meter", response?.data?.odm || "");
    }
  };

  const costSummary = (() => {
    if (!summaryCost.length) return { cost_parts: 0, cost_labor: 0, sub_total: 0 };
    const cost_parts = summaryCost.reduce((sum, s) => sum + parseFloat(s.cost_parts || 0), 0);
    const cost_labor = summaryCost.reduce((sum, s) => sum + parseFloat(s.cost_labor || 0), 0);
    const sub_total = cost_parts + cost_labor;
    return {
      cost_parts: cost_parts.toFixed(2),
      cost_labor: cost_labor.toFixed(2),
      sub_total: sub_total.toFixed(2)
    };
  })();

  const calculateTotal = () => {
    const subTotal = parseFloat(costSummary.sub_total);
    const discount = parseFloat(discountPercent) || 0;
    const tax = 0;
    const discountAmount = subTotal * (discount / 100);
    const taxAmount = subTotal * (tax / 100);
    return (subTotal + taxAmount - discountAmount).toFixed(2);
  };

  const initialValues = {
    asset_id: workOrderByIdData?.asset_id || "",
    reported_by: workOrderByIdData?.issued_by || "",
    assigned_to: workOrderByIdData?.assigned_to || "",
    repair_class: workOrderByIdData?.repair_class || "",
    status: workOrderByIdData?.status || "",
    issued_by: workOrderByIdData?.issued_by || "",
    reported_at: workOrderByIdData?.issued_at ? workOrderByIdData.issued_at.slice(0, 16) : "",
    tags: workOrderByIdData?.tags?.map(tag => ({ value: tag.tag_name, label: tag.tag_name })) || [],
    files: [],
    link_service_ids: workOrderByIdData?.services?.map(s => s.id) || [],
    invoice_no: workOrderByIdData?.invoice_no || "",
    vendor_id: workOrderByIdData?.vendor_id || "",
    issued_at: workOrderByIdData?.issued_at ? workOrderByIdData.issued_at.slice(0, 10) : "",
    scheduled_start_at: workOrderByIdData?.scheduled_start_at ? workOrderByIdData.scheduled_start_at.slice(0, 10) : "",
    expected_completion_at: workOrderByIdData?.expected_completion_at ? workOrderByIdData.expected_completion_at.slice(0, 10) : "",
    finance_status: workOrderByIdData?.finance_status || "",
    operation_status: workOrderByIdData?.operation_status || "",
    finance_approval_by: workOrderByIdData?.finance_approval_by || "",
    operation_approval_by: workOrderByIdData?.operation_approval_by || "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const changedKeys = Object.keys(values).filter((key) => {
        return !_.isEqual(values[key], initialValues[key]);
      });

      const hasNewFiles = uploadedFiles.some((file) => file.previewUrl);

      if (hasNewFiles && !changedKeys.includes("files")) {
        changedKeys.push("files");
      }

      // Validate discount/tax percent
      if (discountPercent < 0 || discountPercent > 100) {
        errorNotify("Discount must be between 0 and 100%");
        return;
      }
      if (taxPercent < 0 || taxPercent > 100) {
        errorNotify("Tax must be between 0 and 100%");
        return;
      }

      const workOrderData = {
        asset_id: parseInt(values.asset_id),
        work_order_id: parseInt(id),
        asset_type: "vehicle",
        repair_class: values.repair_class,
        status: values.status,
        issued_at: values.issued_at,
        issued_by: parseInt(values.issued_by),
        scheduled_start_at: values.scheduled_start_at,
        expected_completion_at: values.expected_completion_at,
        assigned_to: parseInt(values.assigned_to),
        vendor_id: values.vendor_id ? parseInt(values.vendor_id) : null,
        finance_status: values.finance_status,
        operation_status: values.operation_status,
        invoice_no: values.invoice_no,
        tags: values.tags.map((tag) => tag.value || tag),
        files: uploadedFiles.map((file) => file.fileName),
        service_ids: values.link_service_ids,
        updated_keys: changedKeys,
      };

      await dispatch(updateWorkOrder(workOrderData)).unwrap();
      successNotify("Work Order Updated successfully");
      resetForm();
      navigate("/work-orders");
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (workOrderByIdLoading) {
    return (
      <div className="loading_spinner">
        <Spinner animation="grow" />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs values={["Work Order", "Overview", "Work Order Detail"]} />

      <Formik
        initialValues={initialValues}
        validationSchema={WorkOrderValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          submitForm,
        }) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <div className="access_view">
              <h2>Work Order Details</h2>
              <div className="service_entry_btn">
                <Button
                  cancelBtn={() => {
                    navigate(-1);
                  }}
                  loading={isSubmitting || updateWorkOrderLoading}
                  disabled={isSubmitting || updateWorkOrderLoading}
                  submitText="Save Details"
                  type="submit"
                  shouldRow={false}
                  buttonExtraCss="save_issue_details_btn"
                  isShowSubmitBtn={canUpdate}
                />
              </div>
            </div>

            <div className="service_entry_main">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-8">
                    <div className="service_entry_details">
                      <Row>
                        <Col md={6}>
                          <h6>Main Information</h6>
                          <p>Comprehensive information for this work order record.</p>
                        </Col>
                        <Col md={6}>
                          {
                            ((canFinanceApproval && workOrderByIdData?.finance_status === "pending") ||
                              (canOperationalApproval && workOrderByIdData?.operation_status === "pending")) ? (
                              <>
                                <div className="work_order_status">
                                  <div>
                                    <h5>Work Order Status Update</h5>
                                    <p>Please approve or reject the work order.</p>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <button type="button" onClick={() => statusHandler("approved")}>{resolvedIcon} Approve</button>
                                    <button type="button" onClick={() => statusHandler("rejected")}>{closedIcon} Reject</button>
                                  </div>
                                </div>
                              </>
                            ) : null
                          }
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Asset"
                            placeholder="Select Asset"
                            options={vehicleData}
                            loading={loading}
                            name="asset_id"
                            value={vehicleData.find(
                              (option) => option.value === values.asset_id
                            )}
                            onChange={(selectedOption) =>
                              handleVehicleChange(selectedOption, setFieldValue)
                            }
                            onBlur={handleBlur}
                            error={errors.asset_id}
                            touched={touched.asset_id}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Status"
                            placeholder="Select Status"
                            options={workOrderStatusOptions}
                            loading={false}
                            name="status"
                            value={workOrderStatusOptions.find(
                              (option) => option.value === values.status
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "status",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.status}
                            touched={touched.status}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Repair Priority Class"
                            placeholder="Select Repair Priority Class"
                            options={repairClassOptions}
                            loading={false}
                            name="repair_class"
                            value={repairClassOptions.find(
                              (option) => option.value === values.repair_class
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "repair_class",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.repair_class}
                            touched={touched.repair_class}
                            isSearch={false}
                          />
                        </Col>

                        <Col md={6}>
                          <Select
                            label="Issued By"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="issued_by"
                            value={userOptions.find(
                              (option) => option.value === values.issued_by
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "issued_by",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.issued_by}
                            touched={touched.issued_by}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Issued At"
                            placeholder=""
                            type="date"
                            name="issued_at"
                            value={values.issued_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.issued_at}
                            touched={touched.issued_at}
                            disabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Scheduled Start Date"
                            placeholder=""
                            type="date"
                            name="scheduled_start_at"
                            value={values.scheduled_start_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.scheduled_start_at}
                            touched={touched.scheduled_start_at}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Expected Completion Date"
                            placeholder=""
                            type="date"
                            name="expected_completion_at"
                            value={values.expected_completion_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.expected_completion_at}
                            touched={touched.expected_completion_at}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Invoice Number"
                            placeholder="Enter Invoice Number"
                            type="text"
                            name="invoice_no"
                            value={values.invoice_no}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.invoice_no}
                            touched={touched.invoice_no}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Vendor"
                            placeholder="Select Vendor"
                            options={vendorOptions}
                            loading={getVendorsDropdownLoading}
                            name="vendor_id"
                            value={vendorOptions.find(option => option.value === values.vendor_id)}
                            onChange={option => setFieldValue('vendor_id', option?.value || '')}
                            onBlur={handleBlur}
                            error={errors.vendor_id}
                            touched={touched.vendor_id}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Assignee"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="assigned_to"
                            value={userOptions.find(option => option.value === values.assigned_to)}
                            onChange={option => setFieldValue('assigned_to', option?.value || '')}
                            onBlur={handleBlur}
                            error={errors.assigned_to}
                            touched={touched.assigned_to}
                            isSearch={false}
                          />
                        </Col>

                        <Col md={12}>
                          <Select
                            label="Tags"
                            placeholder="Add Tags"
                            options={tagsOptions}
                            loading={getWorkOrderTagsDropdownLoading}
                            name="tags"
                            value={values.tags}
                            onChange={(selectedOptions) =>
                              setFieldValue("tags", selectedOptions || [])
                            }
                            onBlur={handleBlur}
                            isMulti={true}
                            isCreatable={true}
                            error={
                              errors.tags && typeof errors.tags === "string"
                                ? errors.tags
                                : null
                            }
                            touched={touched.tags}
                          />
                        </Col>

                        <Col md={6}>
                          <Input
                            label="Finance Approval Status"
                            placeholder="Select Finance Approval Status"
                            type="text"
                            name="finance_status"
                            value={values.finance_status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.finance_status}
                            touched={touched.finance_status}
                            disabled={true}
                            showIcon={notificationIcon}
                          />
                        </Col>

                        <Col md={6}>
                          <Input
                            label="Operations Approval Status"
                            placeholder="Select Operations Approval Status"
                            type="text"
                            name="operation_status"
                            value={values.operation_status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.operation_status}
                            touched={touched.operation_status}
                            disabled={true}
                            showIcon={notificationIcon}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div className="service_entry_details issue_tracking_main" style={{ marginTop: "20px" }}>
                      <h6>Linked Issues</h6>
                      <p>Link Issues related to this Work Order</p>

                      <div className='issue_tracking_lists'>
                        {
                          linkIssues?.length > 0 ? (
                            linkIssues?.map((issue) => {
                              return (
                                <div key={issue.id} className='issue_tracking_item'>
                                  <div className='d-flex flex-column align-items-start'>
                                    {issue.summery || issue.description || 'No summary available'}
                                    <span>
                                      Issue ID: {issue.service_issue_id} - {formatDate(issue.reported_at)}
                                    </span>
                                  </div>
                                  <div className={`d-flex align-items-center gap-1 status ${issue?.status}`} style={{ cursor: "pointer", textTransform: "capitalize" }}>
                                    {issue?.status === "pending" ? pendingIcon : issue?.status === "open" ? openIcon : issue?.status === "closed" ? closedIcon : issue?.status === "resolved" ? resolvedIcon : issue?.status === "completed" ? resolvedIcon : issue?.status === "in_progress" ? inProgressIcon : null}
                                    {issue?.status}
                                  </div>
                                </div>
                              )
                            })
                          ) : (
                            <div className='no_issues'>
                              <p>No Linked Issues.</p>
                            </div>
                          )
                        }
                      </div>
                    </div>

                    <div className="service_entry_details" style={{ marginTop: "20px" }}>
                      <h6>Service Entry</h6>
                      <p>Link Service Entries related to this Work Order.</p>

                      <Row>
                        <Col md={12}>
                          <Select
                            label="Link Service Entry"
                            placeholder="Please Select Link Service Entry"
                            options={serviceEntryOptions}
                            loading={getServiceEntryByVehicleDropdownLoading}
                            name="link_service_ids"
                            value={serviceEntryOptions?.filter((option) =>
                              values.link_service_ids.includes(option.value)
                            )}
                            onChange={(selectedOptions) => {
                              const selectedIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                              setFieldValue("link_service_ids", selectedIds);

                              const selectedServices = serviceEntryOptions.filter(opt => selectedIds.includes(opt.value));

                              const allIssues = selectedServices.flatMap(s => s.service_issues || []);
                              const allServices = selectedServices.flatMap(s => s.services || []);

                              setSummaryCost(allServices);
                              setLinkIssues(allIssues);
                            }}
                            onBlur={handleBlur}
                            error={errors.link_service_ids}
                            touched={touched.link_service_ids}
                            isSearch={false}
                            isMulti={true}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div className='service_cost_summary_details mt-4' style={{ position: 'relative', top: "0" }}>
                      <div className='service_cost_summary'>
                        <h6>Cost Summary</h6>
                        <p>Financial breakdown of the service entries.</p>
                        <div className='subtotal'>
                          <div>Subtotal (Calculated)</div>
                          <span>PKR {costSummary.sub_total}</span>
                        </div>
                        <div className='service_item'>
                          <div>Parts</div>
                          <div>PKR {costSummary.cost_parts}</div>
                        </div>
                        <div className='service_item'>
                          <div>Labor</div>
                          <div>PKR {costSummary.cost_labor}</div>
                        </div>
                        {/* <div className="service_item">
                          <div>Discount (%)</div>
                          <div>
                            <input
                              className='edit_input'
                              type="number"
                              min={0}
                              max={100}
                              value={discountPercent}
                              onChange={handleDiscountChange}
                              placeholder='0'
                              disabled
                            /> %
                          </div>
                        </div> */}
                        {/* <div className="service_item">
                          <div>Tax (%)</div>
                          <div>
                            <input
                              className='edit_input'
                              type="number"
                              min={0}
                              max={100}
                              value={taxPercent}
                              onChange={handleTaxChange}
                              placeholder='0'
                              disabled
                            /> %
                          </div>
                        </div> */}
                        <div className='total'>
                          <div>Total</div>
                          <span>PKR {calculateTotal()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="photo_document_section mb-3">
                      <h6>Photos & Documents</h6>
                      <p>
                        Upload relevant photos or documents for this service.
                      </p>
                      <FileUpload
                        destination="service-entry"
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                        maxFileSize={10}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <WorkHistory />
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default WorkOrderDetails;
